import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Plus, X, ArrowRight, Shield, Leaf, Compass, Sparkles } from 'lucide-react';
import { Link } from 'react-router-dom';

const iconMap = {
  Shield: Shield,
  Leaf: Leaf,
  Compass: Compass,
  Sparkles: Sparkles
};

const categoriesWithItems = [
  {
    title: "Angst & Fobier",
    icon: "Shield",
    description: "Slip fri af bekymringer, panik og fastlåste tankemønstre. Metakognitiv terapi og hypnose er yderst effektive til at genvinde roen.",
    items: [
      {
        title: "Angst",
        description: "Slip fri af bekymringer, panik og social usikkerhed. Metakognitiv terapi og hypnose er yderst effektive til at genvinde roen i hverdagen.",
        link: "/angst",
        kicker: "Genvind roen"
      },
      {
        title: "Generaliseret angst",
        description: "Slip den konstante alarmtilstand og de vedvarende bekymringer. Jeg arbejder med at stoppe tankemylderet og genfinde roen i både krop og sind.",
        link: "/generaliseret-angst",
        kicker: "Slip bekymringerne"
      },
      {
        title: "Panikangst",
        description: "Få effektive redskaber til at stoppe panikanfald og dæmpe den intense frygt, så du igen kan færdes trygt overalt.",
        link: "/panikangst",
        kicker: "Stop panikken"
      },
      {
        title: "Social angst",
        description: "Få hjælp til at bryde mønstret med selvfokus, bekymringer før sociale situationer og efterfølgende grublerier.",
        link: "/social-angst",
        kicker: "Frihed i sociale situationer"
      },
      {
        title: "Helbredsangst",
        description: "Få hjælp til at bryde mønstret med kropsscanning, bekymringer om sygdom og den konstante søgen efter sikkerhed.",
        link: "/helbredsangst",
        kicker: "Tvivl om kroppen"
      },
      {
        title: "Eksamensangst & præstationsangst",
        description: "Arbejd med bekymring, selvfokus og frygten for at gå blank. Få konkrete redskaber til at håndtere presset før, under og efter præstationen.",
        link: "/eksamensangst-praestationsangst",
        kicker: "Håndter presset"
      },
      {
        title: "Flyskræk",
        description: "Genvind roen i luften og slip frygten for at flyve. Med metakognitiv terapi og hypnose bearbejder vi de ubehagelige tanker og kropslige reaktioner, så du kan rejse trygt.",
        link: "/flyskraek",
        kicker: "Genvind roen i luften"
      },
      {
        title: "OCD & Tvangstanker",
        description: "Bryd de fastlåste mønstre af tvangstanker og ritualer. Jeg arbejder med at give dig kontrollen tilbage over din tid og din frihed.",
        link: "/ocd",
        kicker: "Frihed fra ritualer"
      }
    ]
  },
  {
    title: "Stress & Belastning",
    icon: "Leaf",
    description: "Genvind dit overskud, find roen og få din nattesøvn tilbage med enkle, konkrete redskaber.",
    items: [
      {
        title: "Stress",
        description: "Lær at håndtere presset og bryde de tankemønstre, der holder stressen ved lige. Få konkrete redskaber til at genfinde overskuddet.",
        link: "/stress",
        kicker: "Mental balance"
      },
      {
        title: "Søvnproblemer",
        description: "Få bugt med søvnløshed og tankemylder ved sengetid. Genfind den naturlige nattesøvn gennem effektive metakognitive teknikker.",
        link: "/soevnproblemer",
        kicker: "God nattesøvn"
      },
      {
        title: "Tankemylder",
        description: "Stopper dine tanker aldrig? Lær konkrete teknikker til at lade tankerne passere uden at de overtager din dag og nattesøvn.",
        link: "/tankemylder",
        kicker: "Mental frihed"
      }
    ]
  },
  {
    title: "Andre områder",
    icon: "Compass",
    description: "Professionel støtte og terapeutisk behandling til svære livsomstændigheder og diagnoser.",
    items: [
      {
        title: "Depression",
        description: "Få hjælp til at bryde den tunge spiral af negative tanker og manglende energi. Genfind glæden og handlekraften i dit liv.",
        link: "/depression",
        kicker: "Lysere tanker"
      },
      {
        title: "ADHD",
        description: "Lær at navigere i en hverdag med ADHD. Jeg fokuserer på strategier til at håndtere tankemylder, impulsivitet og eksekutiv funktion.",
        link: "/adhd",
        kicker: "Ro i kaos"
      },
      {
        title: "Autisme",
        description: "Få et tilpasset og autismevenligt forløb med fokus på trivsel, energiregulering, håndtering af sansepres og konkrete hverdagsstrategier.",
        link: "/autismevenlig-terapi",
        kicker: "Trivsel på egne præmisser"
      },
      {
        title: "Borderline",
        description: "Få støtte til at håndtere følelsesmæssig ustabilitet og styrke dine relationer gennem et specialiseret terapeutisk forløb.",
        link: "/borderline",
        kicker: "Stabilitet"
      },
      {
        title: "Traume & PTSD",
        description: "Bearbejd svære oplevelser og få ro i nervesystemet. Jeg arbejder nænsomt med at forløse de kropslige og mentale blokeringer.",
        link: "/traume",
        kicker: "Forløsning"
      },
      {
        title: "Sorg og tab",
        description: "Få støtte til at komme igennem svære perioder, tab eller store livsændringer. Jeg hjælper dig med at finde vejen tilbage til et liv med mening.",
        link: "/sorg-og-tab",
        kicker: "Find fodfæste"
      }
    ]
  },
  {
    title: "Behandlingsformer",
    icon: "Sparkles",
    description: "Målrettede og evidensbaserede metoder, der skaber dybdegående og varige forandringer.",
    items: [
      {
        title: "Metakognitiv Terapi",
        description: "En moderne og evidensbaseret tilgang, hvor jeg fokuserer på, hvordan du forholder dig til dine tanker frem for selve indholdet.",
        link: "/metakognitiv-terapi",
        kicker: "Effektiv metode"
      },
      {
        title: "Hypnose & Hypnoterapi",
        description: "Brug underbevidsthedens kraft til at skabe positive forandringer, bryde vaner eller bearbejde dybereliggende mønstre.",
        link: "/hypnose",
        kicker: "Dyb forandring"
      },
      {
        title: "Parterapi og relationscoaching",
        description: "Hjælp til kommunikation, konflikter, afstand, usikkerhed og gentagne mønstre i parforholdet. Du kan komme alene eller sammen med din partner.",
        link: "/parterapi",
        kicker: "Styrk relationen",
        buttonText: "Læs om parterapi"
      },
      {
        title: "Rygestop",
        description: "Bliv røgfri én gang for alle. Gennem hypnose arbejder jeg med at fjerne trangen og ændre din identitet til en ikke-ryger.",
        link: "/behandlinger/rygestop",
        kicker: "Sund fremtid"
      },
      {
        title: "Online Sessioner",
        description: "Modtag professionel hjælp i dine egne trygge rammer, uanset hvor du befinder dig. Fleksibelt og lige så effektivt som fysisk fremmøde.",
        link: "/online-sessioner",
        kicker: "Hvor som helst"
      }
    ]
  }
];

const Behandlinger = () => {
  const [openIndex, setOpenIndex] = useState(null);

  return (
    <div className="bg-[#FAF7F2] min-h-screen pt-32 pb-24">
      <div className="max-w-6xl mx-auto px-6">
        {/* Header Section */}
        <div className="flex flex-col items-center text-center mb-16 md:mb-24">
          <span className="text-xs font-sans font-bold tracking-[0.2em] uppercase text-[#8C2F12] bg-[#8C2F12]/5 px-4 py-1.5 rounded-full mb-4">
            Specialiseret hjælp
          </span>
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-serif font-medium text-[#123F13] leading-tight mb-6">
            Det kan jeg hjælpe dig med
          </h1>
          <p className="text-lg md:text-xl font-sans text-[#3B352F]/70 leading-relaxed max-w-3xl mx-auto">
            Jeg tilbyder specialiseret behandling inden for en række områder, hvor metakognitiv terapi og hypnose kan skabe mærkbare forandringer i dit liv.
          </p>
        </div>

        {/* Categorized Sections */}
        <div className="space-y-12">
          {categoriesWithItems.map((category, catIndex) => {
            const IconComponent = iconMap[category.icon];
            return (
              <div key={category.title} className="bg-white rounded-2xl border border-[#E8DDCE] p-8 md:p-12 shadow-xs hover:shadow-md transition-shadow duration-300">
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 lg:gap-12">
                  {/* Category Info */}
                  <div className="lg:col-span-1">
                    <div className="flex items-center gap-3 text-[#123F13] mb-4">
                      <div className="w-10 h-10 rounded-full bg-[#FAF7F2] border border-[#E8DDCE] flex items-center justify-center text-[#8C2F12]">
                        {IconComponent && <IconComponent size={20} />}
                      </div>
                      <h2 className="text-2xl font-serif font-semibold text-[#123F13]">
                        {category.title}
                      </h2>
                    </div>
                    <p className="text-sm md:text-base font-sans text-[#3B352F]/70 leading-relaxed">
                      {category.description}
                    </p>
                  </div>

                  {/* Category Accordion Items */}
                  <div className="lg:col-span-2 space-y-2">
                    {category.items.map((item, itemIndex) => {
                      const uniqueId = `${catIndex}-${itemIndex}`;
                      return (
                        <AccordionItem 
                          key={item.title} 
                          item={item} 
                          isOpen={openIndex === uniqueId} 
                          onClick={() => setOpenIndex(openIndex === uniqueId ? null : uniqueId)}
                        />
                      );
                    })}
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Bottom CTA */}
        <div className="mt-20 text-center">
          <Link 
            to="/bestil-tid"
            className="bg-[#123F13] text-white px-8 py-4 rounded-lg font-sans text-base font-semibold transition-all hover:bg-[#123F13]/90 inline-flex items-center gap-2 shadow-lg shadow-[#123F13]/10"
          >
            Book din første session
            <ArrowRight size={18} />
          </Link>
        </div>

      </div>
    </div>
  );
};

const AccordionItem = ({ item, isOpen, onClick }) => {
  return (
    <div className="border-b border-[#E8DDCE]/50 py-4 first:border-t-0 last:border-b-0">
      <button 
        onClick={onClick}
        className="w-full flex justify-between items-center text-left group cursor-pointer py-2"
      >
        <div className="flex flex-col">
          <span className="text-xs font-sans font-semibold tracking-wider text-[#8C2F12] uppercase mb-1">
            {item.kicker}
          </span>
          <span className={`text-xl font-serif transition-colors duration-300 ${isOpen ? 'text-[#8C2F12]' : 'text-[#123F13] group-hover:text-[#8C2F12]'}`}>
            {item.title}
          </span>
        </div>
        <span className="text-[#123F13] group-hover:text-[#8C2F12] transition-colors">
          {isOpen ? <X size={20} strokeWidth={1.5} /> : <Plus size={20} strokeWidth={1.5} />}
        </span>
      </button>
      
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.25, ease: "easeInOut" }}
            className="overflow-hidden"
          >
            <div className="pt-3 pr-4 pb-2">
              <p className="text-sm md:text-base font-sans text-[#3B352F]/80 leading-relaxed mb-4">
                {item.description}
              </p>
              {item.link && (
                <Link 
                  to={item.link}
                  className="bg-[#FAF7F2] text-[#123F13] border border-[#E8DDCE] px-4 py-2 rounded-lg font-sans text-xs font-semibold transition-colors hover:bg-[#F4EFE7] inline-flex items-center gap-2"
                >
                  {item.buttonText || `Læs mere om ${item.title.toLowerCase()}`}
                  <ArrowRight size={12} />
                </Link>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default Behandlinger;
